<script setup lang="ts">
import ArticleText from './ArticleText.vue';
import ArticleCategories from './ArticleCategories.vue';
import ArticleSummary from './ArticleSummary.vue';
import { formatDate } from '~/shared/utils/formatDate';
import { resizeImage, resizeImageSrcsetWidths } from '~/shared/lib/resizeImage';

import ArticleRelative from './ArticleRelative.vue';

import { CLIENT_NEWS_CATEGORY_URL } from '~/shared/api/client/news/news.const';
import type { MaterialDTO } from '~/shared/api/client/news/news.dto';
import ArticleBannerMiddle from '~/entities/article_banners/ui/ArticleBannerMiddle.vue';
import ArticleBannerBoundary from '~/entities/article_banners/ui/ArticleBannerBoundary.vue';

const props = defineProps<MaterialDTO>();

const imageWrapperRef = ref<HTMLDivElement | null>(null);

const $style = useCssModule();

const handleImageLoad = (e: Event) => {
  const image = e.target as HTMLImageElement;
  if (imageWrapperRef.value) {
    imageWrapperRef.value.style.height = `${image.offsetHeight * 2 - 300}px`;
    imageWrapperRef.value.style.marginBottom = `-${image.offsetHeight - 300}px`;
  }
};
</script>

<template>
  <div :class="$style.root">
    <div :class="$style.header">
      <div ref="imageWrapperRef" :class="$style.imageWrapper">
        <figure v-if="props.image?.photo.file_url" :class="$style.image">
          <img
            :src="resizeImage(props.image?.photo.file_url, { width: 1440 })"
            :srcset="
              resizeImageSrcsetWidths(
                props.image?.photo.file_url,
                [640, 960, 1440, 1920, 2880],
              ) || undefined
            "
            sizes="100vw"
            :class="$style.img"
            @load="handleImageLoad"
          />
        </figure>
      </div>
      <div :class="$style.title">
        <div :class="$style.info">
          <ButtonBack :class="$style.back" />
          <div :class="$style.infoWrapper">
            <ArticleCategories
              v-if="props.category"
              :categories="[
                {
                  title: props.category.name,
                  link: `${CLIENT_NEWS_CATEGORY_URL}${props.category.id}`,
                },
              ]"
            />
            <div :class="$style.date">
              {{ formatDate('fullDate', props.publication_date) }}
            </div>
            <!-- <ViewsCounter :class="$style.views" :views="props.views" /> -->
          </div>
        </div>
        <h1 :class="$style.name">{{ props.title }}</h1>

        <ArticleShare :id="props.id" :type="props.type" dark />
      </div>
    </div>
    <div :class="$style.wrapper">
      <div :class="$style.article">
        <ArticleText
          v-if="props.subject"
          :class="$style.excerpt"
          :text="props.subject"
        />

        <ArticleText :class="$style.article__text" :text="props.text">
          <ArticleBannerMiddle />
        </ArticleText>

        <ArticleSummary
          :item="props.author || null"
          :category-name="props.category?.name || false"
          :category-link="
            props.category?.id
              ? `${CLIENT_NEWS_CATEGORY_URL}${props.category.id}`
              : false
          "
        />

        <ArticleRelative
          v-if="props.related_news && props.related_news.length > 0"
          :class="$style.relative"
          :slides="props.related_news"
        />

        <ArticleBannerBoundary :index="0" />
      </div>
    </div>
  </div>
</template>

<style module lang="scss">
.root {
  display: flex;
  flex-direction: column;
  gap: var(--space-2x);
  padding: 0;

  & .image {
    position: sticky;
    top: 64px;
    width: 100%; // Фиксированная ширина контента
    margin: 0 auto; // Центрируем
    padding: 0;
    background-color: var(--color-gray-bg, #f5f5f5);
    overflow: hidden;

    & .img {
      width: 100%;
      height: 100%;
      object-fit: cover; // Возвращаем cover для лучшего заполнения
      object-position: center;
      display: block;
    }
  }

  & .header {
    position: relative;
  }

  & .imageWrapper {
    position: relative;
    z-index: 0;
    width: 100%;
  }

  & .title {
    position: absolute;
    bottom: 50px;
    left: 0;
    right: 0;
    width: 860px;
    margin-left: auto;
    margin-right: auto;
    display: flex;
    flex-direction: column;
    gap: var(--space-2-5x);
    z-index: 1; // Поверх sticky изображения
  }

  & .info {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    gap: var(--space-2-5x);
    width: 100%;
  }

  & .infoWrapper {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    gap: var(--space-2-5x);
    flex-grow: 1;
  }

  & .back {
    color: var(--color-white);

    & path {
      fill: var(--color-white) !important;
    }
  }

  & .date {
    @include text_caption1_caps;
    color: var(--color-white);
    align-items: center;
  }

  & .views {
    color: var(--color-white);

    & path {
      fill: var(--color-white) !important;
    }
  }

  & .name {
    @include text_h1;
    color: var(--color-white);
    margin-top: 0;
    margin-bottom: 0;
  }

  & .wrapper {
    position: relative;
    z-index: 1;
    background-color: var(--color-white);
  }

  & .article {
    display: flex;
    flex-flow: column;
    gap: var(--space-2x);
    width: 860px;
    max-width: 100%;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: var(--space-2-5x);
  }

  & .relative {
    margin-left: -110px;
    margin-right: -110px;
  }

  & .excerpt {
    margin-top: var(--space-3x);
    margin-bottom: var(--space-5x);
    @include text_article_body1_medium;
  }

  & .newsItem {
    border: 1px solid var(--color-gray-line);
    margin-bottom: var(--space-3x);
    margin-top: var(--space-3x);
  }
}

@media (max-width: 1379px) {
  .article {
    width: 100%;
    padding-left: 20px;
    padding-right: 20px;
    border-bottom: none;
  }

  .root .relative {
    margin-left: -20px;
    margin-right: -20px;
  }
}

@media (min-width: 768px) and (max-width: 1379px) {
  .root {
    & .name {
      @include text_h2;
    }
  }
}

@media (max-width: 1023px) {
  .root {
    & .image {
      position: static;
      height: 600px;
    }

    & .imageWrapper {
      height: 600px !important;
      margin-bottom: 0 !important;
    }

    & .title {
      top: 0;
      bottom: 0;
      padding: var(--space-4x);
      max-width: 100%;
    }

    & .info {
      justify-content: space-between;
      align-items: flex-start;
      flex-grow: 1;
    }

    & .infoWrapper {
      flex-grow: 0;
      flex-direction: column;
      align-items: flex-end;
      gap: var(--space-1x);
    }
  }
}

@media (min-width: 600px) and (max-width: 767px) {
  .root {
    & .info {
      gap: var(--space-4x);
    }

    & .wrapper {
      justify-content: space-between;
      width: inherit;
    }
  }
}

@media (max-width: 767px) {
  .root {
    & .name {
      @include text_h3;
    }
  }
}

@media (min-width: 480px) and (max-width: 599px) {
  .article {
    &__info {
      flex-direction: column;
      gap: var(--space-1-5x);

      &__back {
        width: 100%;
        justify-content: flex-start;
      }

      &__wrapper {
        justify-content: space-between;
        width: inherit;
      }
    }
  }
}

@media (max-width: 479px) {
  .root {
    & .artice {
      margin-bottom: var(--space-2x);
    }
  }
}
</style>
